import { useCallback, useMemo, useState } from 'react'

import { CompanyStatusAction } from '../types'

export type CompanyDialogState =
  | { readonly mode: 'add' }
  | { readonly mode: 'edit'; readonly companyIds: readonly string[] }
  | { readonly mode: 'merge'; readonly companyIds: readonly string[] }

export interface StatusConfirmState {
  readonly action: CompanyStatusAction
  readonly companyIds: readonly string[]
}

export interface ModalState {
  readonly companyDialog: CompanyDialogState | null
  readonly isBulkEditOpen: boolean
  readonly statusConfirm: StatusConfirmState | null
  readonly errorMessage: string
  readonly openAddCompany: () => void
  readonly openEditCompany: (companyId: string) => void
  readonly openMergeCompanies: (companyIds: readonly string[]) => void
  readonly closeCompanyDialog: () => void
  readonly openBulkEdit: () => void
  readonly closeBulkEdit: () => void
  readonly openStatusConfirm: (
    action: CompanyStatusAction,
    companyIds: readonly string[],
  ) => void
  readonly closeStatusConfirm: () => void
  readonly setError: (errorMessage: string) => void
}

export function useModalState(): ModalState {
  const [companyDialog, setCompanyDialog] = useState<CompanyDialogState | null>(
    null,
  )
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false)
  const [statusConfirm, setStatusConfirm] = useState<StatusConfirmState | null>(
    null,
  )
  const [errorMessage, setErrorMessage] = useState('')

  const openAddCompany = useCallback(() => {
    setCompanyDialog({ mode: 'add' })
  }, [])

  const openEditCompany = useCallback((companyId: string) => {
    setCompanyDialog({ mode: 'edit', companyIds: [companyId] })
  }, [])

  const openMergeCompanies = useCallback((companyIds: readonly string[]) => {
    setCompanyDialog({ mode: 'merge', companyIds })
  }, [])

  const closeCompanyDialog = useCallback(() => {
    setErrorMessage('')
    setCompanyDialog(null)
  }, [])

  const openBulkEdit = useCallback(() => {
    setIsBulkEditOpen(true)
  }, [])

  const closeBulkEdit = useCallback(() => {
    setErrorMessage('')
    setIsBulkEditOpen(false)
  }, [])

  const openStatusConfirm = useCallback(
    (action: CompanyStatusAction, companyIds: readonly string[]) => {
      setStatusConfirm({ action, companyIds })
    },
    [],
  )

  const closeStatusConfirm = useCallback(() => {
    setErrorMessage('')
    setStatusConfirm(null)
  }, [])

  return useMemo(
    () => ({
      companyDialog,
      isBulkEditOpen,
      statusConfirm,
      errorMessage,
      openAddCompany,
      openEditCompany,
      openMergeCompanies,
      closeCompanyDialog,
      openBulkEdit,
      closeBulkEdit,
      openStatusConfirm,
      closeStatusConfirm,
      setError: setErrorMessage,
    }),
    [
      companyDialog,
      isBulkEditOpen,
      statusConfirm,
      errorMessage,
      openAddCompany,
      openEditCompany,
      openMergeCompanies,
      closeCompanyDialog,
      openBulkEdit,
      closeBulkEdit,
      openStatusConfirm,
      closeStatusConfirm,
    ],
  )
}
